import { Injectable } from '@angular/core';

import { Subject, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { ISP_COLORS } from 'common/colors';

@Injectable({
  providedIn: 'root',
})
export class GlobalPreloaderService {
  private preloaderElement: HTMLElement | undefined;

  private readonly isHiddenEventSubject = new Subject<boolean>();

  private inTransition = false;

  private get isHidden(): boolean {
    return this.preloaderElement?.style.opacity === '0';
  }

  private readonly onTransitionEnd: (event: TransitionEvent) => void =
    event => {
      if (event.propertyName === 'opacity') {
        this.inTransition = false;
        this.isHiddenEventSubject.next(this.isHidden);
      }
    };

  private readonly onTransitionStart: (event: TransitionEvent) => void =
    event => {
      if (event.propertyName === 'opacity') {
        this.inTransition = true;
      }
    };

  set preloadElementId(id: string) {
    const element = document.getElementById(id);

    if (!element) {
      // no element found
      return;
    }

    this.preloaderElement?.removeAllListeners?.();

    this.preloaderElement = element;
    this.preloaderElement.addEventListener(
      'transitionend',
      this.onTransitionEnd,
    );
    this.preloaderElement.addEventListener(
      'transitionstart',
      this.onTransitionStart,
    );
  }

  private hide(): Promise<boolean> {
    if (this.preloaderElement) {
      this.preloaderElement.style.opacity = '0';
      this.preloaderElement.style.pointerEvents = 'none';
    }
    return firstValueFrom(this.isHiddenEventSubject);
  }

  private show(): Promise<boolean> {
    if (this.preloaderElement) {
      this.preloaderElement.style.opacity = '1';
      this.preloaderElement.style.pointerEvents = 'all';
    }
    return firstValueFrom(
      this.isHiddenEventSubject.pipe(map(isHidden => !isHidden)),
    );
  }

  turnOn({ transparent }: { transparent: boolean }): Promise<boolean> {
    if (this.preloaderElement) {
      this.preloaderElement.style.backgroundColor = transparent
        ? ISP_COLORS.backgroundAdditionalTransparent
        : ISP_COLORS.backgroundAdditional;
    }

    if (this.inTransition) {
      return this.show();
    }

    if (!this.isHidden) {
      return Promise.resolve(true);
    }

    return this.show();
  }

  turnOff(): Promise<boolean> {
    if (this.inTransition) {
      return this.hide();
    }

    if (this.isHidden) {
      return Promise.resolve(true);
    }

    return this.hide();
  }
}
